import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowLeft, Info, RotateCcw } from 'lucide-react'
import { Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { PeriodSelector } from '@/components/analytics'
import { supabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { formatMarketingTargetologistName } from '@/lib/marketingTargetologistName'
import {
  buildDraftFromWeeklyPlanRows,
  flattenDraftToWeeklyPlanRows,
  getEffectiveWeeklyPlan,
  getPartnerMarketingTargetKey,
  type EffectiveWeeklyPlan,
  type WeeklyPlanDraft,
  type WeeklyPlanEntry,
  type WeeklyPlanningStatus
} from '@/lib/partnerMarketingWeeklyPlan'
import { formatCurrency } from '@/lib/utils'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useMetricWeeks, type MetricWeek } from '@/hooks/useMetricWeeks'
import { SURFACE_CARD_CLASS, SUBTLE_PANEL_CLASS, INPUT_CLASS } from '@/components/marketing/marketingClasses'


type ScenarioMode = 'keep' | 'rebalance' | 'average' | null
type PlannerTab = 'all' | WeeklyPlanningStatus

interface ClickDailyRow {
  rowKey: string
  date: string
  service: string
  accountId: number
  accountName: string | null
  accountCurrency: string | null
  cost: number
  conversions: number
}

interface WeeklyFactSummary {
  leads: number
  spend: number
  spendByCurrency: Record<string, number>
}

interface WeeklyPlanAverageSummary {
  leads: number | null
  spend: number | null
}

interface PeriodRange {
  period_id: number
  period_start: string
  period_end: string
  period_len_days: number
  month_number?: number
  year_number?: number
  week_start?: number
  week_end?: number
}

interface TargetologistSummary {
  key: string
  accountId: number
  service: string
  label: string
  currency: string | null
  searchText: string
}

function normalizeCurrencyCode(value: string | null | undefined): string | null {
  if (!value) return null
  const normalized = value.trim().toUpperCase()
  return normalized || null
}

function getTargetologistLabel(row: ClickDailyRow): string {
  return formatMarketingTargetologistName(row.accountName) || `Таргетолог ${row.accountId}`
}

function getTargetologistKey(service: string, accountId: number): string {
  return getPartnerMarketingTargetKey(service, accountId)
}

function parseTargetologistKey(targetKey: string): { service: string; accountId: number } | null {
  const separatorIndex = targetKey.lastIndexOf(':')
  if (separatorIndex <= 0) return null

  const service = targetKey.slice(0, separatorIndex)
  const accountId = Number(targetKey.slice(separatorIndex + 1))
  if (!service || !Number.isFinite(accountId)) return null

  return { service, accountId }
}

function getTodayIso(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatNumber(value: number): string {
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })
}

function formatMoneyValue(value: number, currency: string | null): string {
  if (currency) return formatCurrency(value, currency)
  return formatNumber(value)
}

function formatMoneyByCurrencies(spendByCurrency: Record<string, number>): string {
  const currencies = Object.keys(spendByCurrency).sort((a, b) => a.localeCompare(b, 'en'))

  if (currencies.length === 0) {
    return formatMoneyValue(0, null)
  }

  if (currencies.length === 1) {
    const currency = currencies[0]
    return formatMoneyValue(spendByCurrency[currency] || 0, currency)
  }

  return currencies
    .map((currency) => formatMoneyValue(spendByCurrency[currency] || 0, currency))
    .join(' • ')
}

function formatShortDate(value: string): string {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}`
}

function formatWeekLabel(week: MetricWeek): string {
  return `Неделя ${week.week_number_in_period}`
}

function formatLeadLabel(value: number | null): string {
  if (value === null) return '—'
  return `${formatNumber(value)} лида`
}

function computePeriodWeeks(period: {
  period_id: number
  period_start: string
  period_end: string
}): MetricWeek[] {
  const result: MetricWeek[] = []
  const startDate = new Date(`${period.period_start}T00:00:00`)
  const endDate = new Date(`${period.period_end}T00:00:00`)
  let cursor = new Date(startDate)
  let weekNumber = 1

  const fmt = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  while (cursor <= endDate) {
    const weekStart = new Date(cursor)
    const weekEnd = new Date(cursor)
    weekEnd.setDate(weekEnd.getDate() + 6)
    if (weekEnd > endDate) {
      weekEnd.setTime(endDate.getTime())
    }

    const weekLenDays = Math.floor((weekEnd.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
    result.push({
      week_id: -weekNumber,
      period_id: period.period_id,
      week_number_in_period: weekNumber,
      week_start: fmt(weekStart),
      week_end: fmt(weekEnd),
      week_len_days: weekLenDays
    })

    cursor = new Date(weekEnd)
    cursor.setDate(cursor.getDate() + 1)
    weekNumber += 1
  }

  return result
}

function parseNumericInput(value: string): number | null {
  if (!value.trim()) return null
  const normalized = value.replace(/\s/g, '').replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function createEmptyFactSummary(): WeeklyFactSummary {
  return {
    leads: 0,
    spend: 0,
    spendByCurrency: {}
  }
}

function roundMetricValue(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

function calculateCostPerLead(spend: number | null, leads: number | null): number | null {
  if (spend === null || leads === null || leads <= 0) return null
  return roundMetricValue(spend / leads)
}

function toWeeklyAverageFactSummary(summary: WeeklyFactSummary, weeksCount: number): WeeklyFactSummary {
  if (weeksCount <= 0) {
    return createEmptyFactSummary()
  }

  return {
    leads: roundMetricValue(summary.leads / weeksCount),
    spend: roundMetricValue(summary.spend / weeksCount),
    spendByCurrency: Object.fromEntries(
      Object.entries(summary.spendByCurrency).map(([currency, value]) => [currency, roundMetricValue(value / weeksCount)])
    )
  }
}

function hasNonZeroMarketingWeek(summary: WeeklyFactSummary | null | undefined): boolean {
  if (!summary) return false
  return summary.leads > 0 || summary.spend > 0
}

function buildWeeklyFactsByTarget(
  rows: ClickDailyRow[],
  periodWeeks: MetricWeek[]
): Map<string, Record<number, WeeklyFactSummary>> {
  const map = new Map<string, Record<number, WeeklyFactSummary>>()

  rows.forEach((row) => {
    const week = periodWeeks.find((item) => row.date >= item.week_start && row.date <= item.week_end)
    if (!week) return

    const targetKey = getTargetologistKey(row.service, row.accountId)
    const targetFacts = map.get(targetKey) || {}
    const currentFact = targetFacts[week.week_id] || createEmptyFactSummary()
    currentFact.leads += row.conversions
    currentFact.spend += row.cost
    if (row.accountCurrency) {
      currentFact.spendByCurrency[row.accountCurrency] = (currentFact.spendByCurrency[row.accountCurrency] || 0) + row.cost
    }
    targetFacts[week.week_id] = currentFact
    map.set(targetKey, targetFacts)
  })

  return map
}

function mapClickDailyRows(data: any[] | null | undefined): ClickDailyRow[] {
  return ((data || []) as any[]).map((item, index) => ({
    rowKey: [
      item.metric_date,
      item.account_id,
      item.service,
      item.cost ?? 0,
      item.conversions ?? 0,
      index
    ].join(':'),
    date: item.metric_date,
    service: item.service || 'Без сервиса',
    accountId: Number(item.account_id),
    accountName: item.account_name || null,
    accountCurrency: normalizeCurrencyCode(item.account_currency),
    cost: Number(item.cost || 0),
    conversions: Number(item.conversions || 0)
  })) as ClickDailyRow[]
}

function allocateProportionally(total: number, weights: number[]): number[] {
  if (weights.length === 0) return []

  const totalWeight = weights.reduce((sum, weight) => sum + Math.max(weight, 0), 0)
  if (totalWeight <= 0) {
    const evenShare = total / weights.length
    return weights.map((_, index) => {
      const raw = index === weights.length - 1
        ? total - evenShare * (weights.length - 1)
        : evenShare
      return Math.round((raw + Number.EPSILON) * 100) / 100
    })
  }

  let allocated = 0
  return weights.map((weight, index) => {
    const raw = index === weights.length - 1
      ? total - allocated
      : Math.round((((Math.max(weight, 0) / totalWeight) * total) + Number.EPSILON) * 100) / 100
    allocated += raw
    return Math.round((raw + Number.EPSILON) * 100) / 100
  })
}

function statusLabel(status: WeeklyPlanningStatus): string {
  if (status === 'planned') return 'Планируем'
  if (status === 'excluded') return 'Исключили'
  return 'План не задан'
}

function statusBadgeClass(status: WeeklyPlanningStatus): string {
  if (status === 'planned') return 'bg-emerald-100 text-emerald-800 border border-emerald-200'
  if (status === 'excluded') return 'bg-slate-200 text-slate-700 border border-slate-300'
  return 'bg-amber-100 text-amber-800 border border-amber-200'
}

function PreviousPeriodMetricHint({
  planValue,
  factValue,
  fact3PeriodsValue
}: {
  planValue: string
  factValue: string
  fact3PeriodsValue: string
}) {
  return (
    <div className="flex items-center gap-1 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground flex-wrap">
      <span className="relative inline-flex items-center gap-1 group text-muted-foreground">
        <Info className="h-3 w-3 shrink-0" />
        <span className="absolute left-0 top-full z-10 mt-1 hidden w-56 whitespace-normal rounded bg-foreground px-2 py-1.5 text-[11px] leading-snug text-background shadow group-hover:block">
          План считается по заполненным плановым неделям прошлого периода. Факт и 3 периода показывают недельное среднее.
        </span>
      </span>
      <span>План: {planValue}</span>
      <span className="text-muted-foreground/40">|</span>
      <span>Факт: {factValue}</span>
      <span className="text-muted-foreground/40">|</span>
      <span>3 мес: {fact3PeriodsValue}</span>
    </div>
  )
}

function buildAveragePlansByTarget(
  targetKeys: string[],
  periodWeeks: MetricWeek[],
  periodPlanDraft: WeeklyPlanDraft
): Map<string, WeeklyPlanAverageSummary> {
  if (periodWeeks.length === 0) {
    return new Map()
  }

  const result = new Map<string, WeeklyPlanAverageSummary>()

  targetKeys.forEach((targetKey) => {
    let leadsTotal = 0
    let spendTotal = 0
    let activeWeeks = 0

    periodWeeks.forEach((week) => {
      const plan = getEffectiveWeeklyPlan(
        targetKey,
        week.week_number_in_period,
        periodPlanDraft,
        periodWeeks
      )

      if (plan.status !== 'planned') return
      if ((plan.leads ?? 0) <= 0 && (plan.spend ?? 0) <= 0) return

      activeWeeks += 1

      if (plan.leads !== null) {
        leadsTotal += plan.leads
      }

      if (plan.spend !== null) {
        spendTotal += plan.spend
      }
    })

    result.set(targetKey, {
      leads: activeWeeks > 0 ? roundMetricValue(leadsTotal / activeWeeks) : null,
      spend: activeWeeks > 0 ? roundMetricValue(spendTotal / activeWeeks) : null
    })
  })

  return result
}

function getWeekVisualState(week: MetricWeek, todayIso: string): 'past' | 'current' | 'future' {
  if (week.week_end < todayIso) return 'past'
  if (week.week_start > todayIso) return 'future'
  return 'current'
}

export function PartnerMarketingPlanningPage() {
  const { partnerNumber, studioId } = useParams<{ partnerNumber: string; studioId?: string }>()
  const parsedPartnerNumber = Number(partnerNumber)
  const parsedStudioId = studioId ? Number(studioId) : undefined
  const periods = useStudioPeriods()
  const { currentWeek } = useMetricWeeks(periods.currentPeriod, periods.isCurrentPeriod)

  const [weeks, setWeeks] = useState<MetricWeek[]>([])
  const [rows, setRows] = useState<ClickDailyRow[]>([])
  const [previousPeriodRows, setPreviousPeriodRows] = useState<ClickDailyRow[]>([])
  const [threePeriodsRows, setThreePeriodsRows] = useState<ClickDailyRow[]>([])
  const [previousPeriodWeeks, setPreviousPeriodWeeks] = useState<MetricWeek[]>([])
  const [threePeriodsWeeks, setThreePeriodsWeeks] = useState<MetricWeek[]>([])
  const [previousPeriodPlanDraft, setPreviousPeriodPlanDraft] = useState<WeeklyPlanDraft>({})
  const [loading, setLoading] = useState(true)
  const [plansLoading, setPlansLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<number>(1)
  const [activeTab, setActiveTab] = useState<PlannerTab>('all')
  const [selectedTargetKey, setSelectedTargetKey] = useState<string | null>(null)
  const [savedDraft, setSavedDraft] = useState<WeeklyPlanDraft>({})
  const [draft, setDraft] = useState<WeeklyPlanDraft>({})
  const [selectedScenarioMode, setSelectedScenarioMode] = useState<ScenarioMode>(null)
  const confirmLeaveWithUnsavedChanges = () => {
    if (!hasUnsavedChanges) return true
    return window.confirm('Есть несохранённые изменения. Выйти без сохранения?')
  }
  const previousPeriod = useMemo(
    () => periods.currentPeriodIndex > 0 ? periods.availablePeriods[periods.currentPeriodIndex - 1] || null : null,
    [periods.availablePeriods, periods.currentPeriodIndex]
  )
  const threePreviousPeriods = useMemo<PeriodRange[]>(
    () => periods.currentPeriodIndex > 0
      ? (periods.availablePeriods
        .slice(Math.max(0, periods.currentPeriodIndex - 3), periods.currentPeriodIndex) as PeriodRange[])
      : [],
    [periods.availablePeriods, periods.currentPeriodIndex]
  )

  useEffect(() => {
    if (!periods.currentPeriod) {
      setWeeks([])
      return
    }

    let cancelled = false

    const loadWeeks = async () => {
      try {
        const { data, error: weeksError } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('*')
            .eq('period_id', periods.currentPeriod!.period_id)
            .order('week_number_in_period', { ascending: true })
        )

        if (cancelled) return
        if (weeksError) throw weeksError

        const periodWeeks = (data as MetricWeek[] | null) || []
        setWeeks(periodWeeks.length > 0 ? periodWeeks : computePeriodWeeks(periods.currentPeriod!))
      } catch (weekError) {
        console.error('❌ PartnerMarketingPlanningPage weeks load failed:', weekError)
        if (!cancelled) {
          setWeeks(computePeriodWeeks(periods.currentPeriod!))
        }
      }
    }

    loadWeeks()

    return () => {
      cancelled = true
    }
  }, [periods.currentPeriod])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber)) {
      setLoading(false)
      setRows([])
      setPreviousPeriodRows([])
      setThreePeriodsRows([])
      setError('Не удалось определить франчайзи')
      return
    }

    if (periods.loading) return

    if (!periods.currentPeriod) {
      setLoading(false)
      setRows([])
      setPreviousPeriodRows([])
      setThreePeriodsRows([])
      setError(periods.error ? 'Не удалось загрузить период аналитики' : null)
      return
    }

    let cancelled = false

    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        const result = await withAuthRecovery(() =>
          (supabase as any).rpc('get_partner_click_stats', {
            p_partner_number: parsedPartnerNumber,
            p_date_from: periods.currentPeriod!.period_start,
            p_date_to: periods.currentPeriod!.period_end
          })
        )

        if (result.error) throw result.error

        const mappedRows = mapClickDailyRows(result.data)
        let mappedPreviousPeriodRows: ClickDailyRow[] = []
        let mappedThreePeriodsRows: ClickDailyRow[] = []

        if (previousPeriod) {
          try {
            const previousPeriodResult = await withAuthRecovery(() =>
              (supabase as any).rpc('get_partner_click_stats', {
                p_partner_number: parsedPartnerNumber,
                p_date_from: previousPeriod.period_start,
                p_date_to: previousPeriod.period_end
              })
            )

            if (previousPeriodResult.error) throw previousPeriodResult.error
            mappedPreviousPeriodRows = mapClickDailyRows(previousPeriodResult.data)
          } catch (previousPeriodError) {
            console.error('❌ PartnerMarketingPlanningPage previous period load failed:', previousPeriodError)
          }
        }

        if (threePreviousPeriods.length > 0) {
          try {
            const rangeStart = threePreviousPeriods[0]?.period_start
            const rangeEnd = threePreviousPeriods[threePreviousPeriods.length - 1]?.period_end

            if (rangeStart && rangeEnd) {
              const threePeriodsResult = await withAuthRecovery(() =>
                (supabase as any).rpc('get_partner_click_stats', {
                  p_partner_number: parsedPartnerNumber,
                  p_date_from: rangeStart,
                  p_date_to: rangeEnd
                })
              )

              if (threePeriodsResult.error) throw threePeriodsResult.error
              mappedThreePeriodsRows = mapClickDailyRows(threePeriodsResult.data)
            }
          } catch (threePeriodsError) {
            console.error('❌ PartnerMarketingPlanningPage three periods load failed:', threePeriodsError)
          }
        }

        if (!cancelled) {
          setRows(mappedRows)
          setPreviousPeriodRows(mappedPreviousPeriodRows)
          setThreePeriodsRows(mappedThreePeriodsRows)
        }
      } catch (loadError) {
        console.error('❌ PartnerMarketingPlanningPage load failed:', loadError)
        if (!cancelled) {
          setError('Не удалось загрузить маркетинговые данные для планирования')
          setRows([])
          setPreviousPeriodRows([])
          setThreePeriodsRows([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadData()

    return () => {
      cancelled = true
    }
  }, [parsedPartnerNumber, periods.currentPeriod, periods.error, periods.loading, previousPeriod, threePreviousPeriods])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber) || !previousPeriod) {
      setPreviousPeriodWeeks([])
      setPreviousPeriodPlanDraft({})
      return
    }

    let cancelled = false

    const loadPreviousPeriodPlan = async () => {
      try {
        const { data: previousWeeksData, error: previousWeeksError } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('*')
            .eq('period_id', previousPeriod.period_id)
            .order('week_number_in_period', { ascending: true })
        )

        if (previousWeeksError) throw previousWeeksError
        if (cancelled) return

        const resolvedPreviousWeeks = ((previousWeeksData as MetricWeek[] | null) || [])
        const normalizedPreviousWeeks = resolvedPreviousWeeks.length > 0
          ? resolvedPreviousWeeks
          : computePeriodWeeks(previousPeriod)

        const previousWeekIds = resolvedPreviousWeeks
          .map((week) => week.week_id)
          .filter((weekId) => weekId > 0)

        let previousDraft: WeeklyPlanDraft = {}

        if (previousWeekIds.length > 0) {
          const { data: previousPlanRows, error: previousPlanError } = await withAuthRecovery(() =>
            (supabase as any)
              .from('click_weekly_plan')
              .select('partner_number, week_id, service, account_id, planning_status, leads_plan, spend_plan, updated_at, updated_by_user_id')
              .eq('partner_number', parsedPartnerNumber)
              .in('week_id', previousWeekIds)
          )

          if (previousPlanError) throw previousPlanError
          if (cancelled) return

          previousDraft = buildDraftFromWeeklyPlanRows((previousPlanRows || []) as any[], normalizedPreviousWeeks)
        }

        if (!cancelled) {
          setPreviousPeriodWeeks(normalizedPreviousWeeks)
          setPreviousPeriodPlanDraft(previousDraft)
        }
      } catch (previousPlanLoadError) {
        console.error('❌ PartnerMarketingPlanningPage previous period plan load failed:', previousPlanLoadError)
        if (!cancelled) {
          setPreviousPeriodWeeks([])
          setPreviousPeriodPlanDraft({})
        }
      }
    }

    loadPreviousPeriodPlan()

    return () => {
      cancelled = true
    }
  }, [parsedPartnerNumber, previousPeriod])

  useEffect(() => {
    if (threePreviousPeriods.length === 0) {
      setThreePeriodsWeeks([])
      return
    }

    let cancelled = false

    const loadThreePeriodsWeeks = async () => {
      try {
        const periodIds = threePreviousPeriods.map((period) => period.period_id)
        const { data: weeksData, error: weeksError } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('*')
            .in('period_id', periodIds)
            .order('period_id', { ascending: true })
            .order('week_number_in_period', { ascending: true })
        )

        if (weeksError) throw weeksError
        if (cancelled) return

        const resolvedWeeks = ((weeksData as MetricWeek[] | null) || [])
        setThreePeriodsWeeks(
          resolvedWeeks.length > 0
            ? resolvedWeeks
            : threePreviousPeriods.flatMap((period) => computePeriodWeeks(period))
        )
      } catch (threePeriodsWeeksError) {
        console.error('❌ PartnerMarketingPlanningPage three periods weeks load failed:', threePeriodsWeeksError)
        if (!cancelled) {
          setThreePeriodsWeeks(threePreviousPeriods.flatMap((period) => computePeriodWeeks(period)))
        }
      }
    }

    loadThreePeriodsWeeks()

    return () => {
      cancelled = true
    }
  }, [threePreviousPeriods])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber) || weeks.length === 0) {
      setPlansLoading(false)
      setSavedDraft({})
      setDraft({})
      return
    }

    let cancelled = false

    const loadPlans = async () => {
      setPlansLoading(true)

      try {
        const weekIds = weeks
          .map((week) => week.week_id)
          .filter((weekId) => weekId > 0)

        if (weekIds.length === 0) {
          if (!cancelled) {
            setSavedDraft({})
            setDraft({})
          }
          return
        }

        const { data, error: plansError } = await withAuthRecovery(() =>
          (supabase as any)
            .from('click_weekly_plan')
            .select('partner_number, week_id, service, account_id, planning_status, leads_plan, spend_plan, updated_at, updated_by_user_id')
            .eq('partner_number', parsedPartnerNumber)
            .in('week_id', weekIds)
        )

        if (plansError) throw plansError
        if (cancelled) return

        const parsedDraft = buildDraftFromWeeklyPlanRows((data || []) as any[], weeks)
        setSavedDraft(parsedDraft)
        setDraft(parsedDraft)
      } catch (loadPlansError) {
        console.error('❌ PartnerMarketingPlanningPage plans load failed:', loadPlansError)
        if (!cancelled) {
          setSavedDraft({})
          setDraft({})
        }
      } finally {
        if (!cancelled) {
          setPlansLoading(false)
        }
      }
    }

    loadPlans()

    return () => {
      cancelled = true
    }
  }, [parsedPartnerNumber, weeks])

  useEffect(() => {
    if (currentWeek?.week_number_in_period) {
      setSelectedWeekNumber(currentWeek.week_number_in_period)
      return
    }
    if (weeks.length > 0) {
      setSelectedWeekNumber((current) => (
        weeks.some((week) => week.week_number_in_period === current)
          ? current
          : weeks[0].week_number_in_period
      ))
    }
  }, [currentWeek?.week_number_in_period, weeks])

  const targetologists = useMemo<TargetologistSummary[]>(() => {
    const map = new Map<string, TargetologistSummary>()

    const addFromRow = (row: ClickDailyRow) => {
      const key = getTargetologistKey(row.service, row.accountId)
      if (map.has(key)) return
      map.set(key, {
        key,
        accountId: row.accountId,
        service: row.service,
        label: getTargetologistLabel(row),
        currency: row.accountCurrency,
        searchText: `${getTargetologistLabel(row)} ${row.service} ${row.accountId} ${row.accountName || ''}`
      })
    }

    rows.forEach(addFromRow)
    previousPeriodRows.forEach(addFromRow)
    threePeriodsRows.forEach(addFromRow)

    const addFromDraft = (targetKey: string) => {
      if (map.has(targetKey)) return
      const parsed = parseTargetologistKey(targetKey)
      if (!parsed) return

      const label = `Таргетолог ${parsed.accountId}`
      map.set(targetKey, {
        key: targetKey,
        accountId: parsed.accountId,
        service: parsed.service,
        label,
        currency: null,
        searchText: `${label} ${parsed.service} ${parsed.accountId}`
      })
    }

    Object.keys(savedDraft).forEach(addFromDraft)
    Object.keys(draft).forEach(addFromDraft)

    return Array.from(map.values()).sort(
      (a, b) => a.label.localeCompare(b.label, 'ru') || a.service.localeCompare(b.service, 'ru')
    )
  }, [draft, previousPeriodRows, rows, savedDraft, threePeriodsRows])

  const weeklyFactsByTarget = useMemo(() => {
    const map = new Map<string, Record<number, WeeklyFactSummary>>()

    rows.forEach((row) => {
      const week = weeks.find((item) => row.date >= item.week_start && row.date <= item.week_end)
      if (!week) return

      const targetKey = getTargetologistKey(row.service, row.accountId)
      const targetFacts = map.get(targetKey) || {}
      const currentFact = targetFacts[week.week_number_in_period] || createEmptyFactSummary()
      currentFact.leads += row.conversions
      currentFact.spend += row.cost
      if (row.accountCurrency) {
        currentFact.spendByCurrency[row.accountCurrency] = (currentFact.spendByCurrency[row.accountCurrency] || 0) + row.cost
      }
      targetFacts[week.week_number_in_period] = currentFact
      map.set(targetKey, targetFacts)
    })

    return map
  }, [rows, weeks])

  const previousPeriodAverageFactsByTarget = useMemo(() => {
    if (!previousPeriod || previousPeriodWeeks.length === 0) {
      return new Map<string, WeeklyFactSummary>()
    }

    const weeklyFacts = buildWeeklyFactsByTarget(previousPeriodRows, previousPeriodWeeks)

    const result = new Map<string, WeeklyFactSummary>()

    Array.from(weeklyFacts.entries()).forEach(([targetKey, factsByWeek]) => {
      const summaries = Object.values(factsByWeek).filter(hasNonZeroMarketingWeek)
      if (summaries.length === 0) return

      const totalFact = summaries.reduce((acc, summary) => {
        acc.leads += summary.leads
        acc.spend += summary.spend
        Object.entries(summary.spendByCurrency).forEach(([currency, value]) => {
          acc.spendByCurrency[currency] = (acc.spendByCurrency[currency] || 0) + value
        })
        return acc
      }, createEmptyFactSummary())

      result.set(targetKey, toWeeklyAverageFactSummary(totalFact, summaries.length))
    })

    return result
  }, [previousPeriod, previousPeriodRows, previousPeriodWeeks])

  const threePeriodsAverageFactsByTarget = useMemo(() => {
    if (threePreviousPeriods.length === 0 || threePeriodsWeeks.length === 0) {
      return new Map<string, WeeklyFactSummary>()
    }

    const weeksByPeriodId = new Map<number, MetricWeek[]>()
    threePeriodsWeeks.forEach((week) => {
      const existing = weeksByPeriodId.get(week.period_id) || []
      existing.push(week)
      weeksByPeriodId.set(week.period_id, existing)
    })

    const weeklyFacts = buildWeeklyFactsByTarget(threePeriodsRows, threePeriodsWeeks)

    const result = new Map<string, WeeklyFactSummary>()

    Array.from(weeklyFacts.entries()).forEach(([targetKey, factsByWeek]) => {
      const periodsHaveData = threePreviousPeriods.every((period) => {
        const periodWeeks = weeksByPeriodId.get(period.period_id) || []
        return periodWeeks.some((week) => hasNonZeroMarketingWeek(factsByWeek[week.week_id]))
      })

      if (!periodsHaveData) return

      const summaries = Object.values(factsByWeek).filter(hasNonZeroMarketingWeek)
      if (summaries.length === 0) return

      const totalFact = summaries.reduce((acc, summary) => {
        acc.leads += summary.leads
        acc.spend += summary.spend
        Object.entries(summary.spendByCurrency).forEach(([currency, value]) => {
          acc.spendByCurrency[currency] = (acc.spendByCurrency[currency] || 0) + value
        })
        return acc
      }, createEmptyFactSummary())

      result.set(targetKey, toWeeklyAverageFactSummary(totalFact, summaries.length))
    })

    return result
  }, [threePeriodsRows, threePeriodsWeeks, threePreviousPeriods])

  const previousPeriodAveragePlansByTarget = useMemo(() => {
    return buildAveragePlansByTarget(
      targetologists.map((target) => target.key),
      previousPeriodWeeks,
      previousPeriodPlanDraft
    )
  }, [previousPeriodPlanDraft, previousPeriodWeeks, targetologists])

  const weekStatesByTarget = useMemo(() => {
    const result = new Map<string, Record<number, EffectiveWeeklyPlan>>()
    targetologists.forEach((target) => {
      const perWeek: Record<number, EffectiveWeeklyPlan> = {}
      weeks.forEach((week) => {
        perWeek[week.week_number_in_period] = getEffectiveWeeklyPlan(target.key, week.week_number_in_period, draft, weeks)
      })
      result.set(target.key, perWeek)
    })
    return result
  }, [draft, targetologists, weeks])

  const selectedWeek = useMemo(
    () => weeks.find((week) => week.week_number_in_period === selectedWeekNumber) || null,
    [selectedWeekNumber, weeks]
  )
  const todayIso = useMemo(() => getTodayIso(), [])
  const selectedWeekVisualState = selectedWeek ? getWeekVisualState(selectedWeek, todayIso) : null
  const selectedWeekIsReadOnly = selectedWeekVisualState === 'past'

  const rowsForSelectedWeek = useMemo(() => {
    if (!selectedWeek) return []

    return targetologists.map((target) => {
      const currentPlan = weekStatesByTarget.get(target.key)?.[selectedWeek.week_number_in_period] || {
        status: 'unset',
        leads: null,
        spend: null
      }
      const referenceFact = previousPeriodAverageFactsByTarget.get(target.key) || null

      return {
        ...target,
        status: currentPlan.status,
        planLeads: currentPlan.leads,
        planSpend: currentPlan.spend,
        factLeads: referenceFact?.leads ?? null,
        factSpendByCurrency: referenceFact?.spendByCurrency ?? null
      }
    })
  }, [previousPeriodAverageFactsByTarget, selectedWeek, targetologists, weekStatesByTarget])

  const tabCounts = useMemo(() => ({
    planned: rowsForSelectedWeek.filter((row) => row.status === 'planned').length,
    unset: rowsForSelectedWeek.filter((row) => row.status === 'unset').length,
    excluded: rowsForSelectedWeek.filter((row) => row.status === 'excluded').length
  }), [rowsForSelectedWeek])

  const visibleRows = useMemo(() => {
    if (activeTab === 'all') return rowsForSelectedWeek
    return rowsForSelectedWeek.filter((row) => row.status === activeTab)
  }, [activeTab, rowsForSelectedWeek])

  useEffect(() => {
    if (visibleRows.length === 0) {
      setSelectedTargetKey(null)
      return
    }

    setSelectedTargetKey((current) => (
      current && visibleRows.some((row) => row.key === current)
        ? current
        : visibleRows[0].key
    ))
  }, [visibleRows])

  const selectedTarget = useMemo(
    () => targetologists.find((target) => target.key === selectedTargetKey) || null,
    [selectedTargetKey, targetologists]
  )

  const selectedTargetWeekStates = useMemo(
    () => selectedTarget ? (weekStatesByTarget.get(selectedTarget.key) || {}) : {},
    [selectedTarget, weekStatesByTarget]
  )

  const selectedTargetFacts = useMemo(
    () => selectedTarget ? (weeklyFactsByTarget.get(selectedTarget.key) || {}) : {},
    [selectedTarget, weeklyFactsByTarget]
  )

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(savedDraft),
    [draft, savedDraft]
  )

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return
      event.preventDefault()
      event.returnValue = ''
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (!hasUnsavedChanges) return

      const target = event.target
      if (!(target instanceof Element)) return

      const anchor = target.closest('a[href]')
      if (!(anchor instanceof HTMLAnchorElement)) return
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) return

      const nextUrl = new URL(anchor.href, window.location.href)
      const currentUrl = new URL(window.location.href)

      if (
        nextUrl.origin === currentUrl.origin &&
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash
      ) {
        return
      }

      if (!window.confirm('Есть несохранённые изменения. Выйти без сохранения?')) {
        event.preventDefault()
        event.stopPropagation()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('click', handleDocumentClick, true)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleDocumentClick, true)
    }
  }, [hasUnsavedChanges])

  const selectedWeekPlan = selectedTarget && selectedWeek
    ? selectedTargetWeekStates[selectedWeek.week_number_in_period] || { status: 'unset', leads: null, spend: null }
    : null

  const selectedPreviousPeriodAverageFact = selectedTarget
    ? previousPeriodAverageFactsByTarget.get(selectedTarget.key) || null
    : null

  const selectedPreviousPeriodAveragePlan = selectedTarget
    ? previousPeriodAveragePlansByTarget.get(selectedTarget.key) || null
    : null

  const selectedThreePeriodsAverageFact = selectedTarget
    ? threePeriodsAverageFactsByTarget.get(selectedTarget.key) || null
    : null

  const selectedWeekPlanCostPerLead = useMemo(
    () => calculateCostPerLead(selectedWeekPlan?.spend ?? null, selectedWeekPlan?.leads ?? null),
    [selectedWeekPlan?.leads, selectedWeekPlan?.spend]
  )

  const selectedPreviousPeriodAveragePlanCostPerLead = useMemo(
    () => calculateCostPerLead(selectedPreviousPeriodAveragePlan?.spend ?? null, selectedPreviousPeriodAveragePlan?.leads ?? null),
    [selectedPreviousPeriodAveragePlan?.leads, selectedPreviousPeriodAveragePlan?.spend]
  )

  const selectedPreviousPeriodAverageFactCostPerLead = useMemo(
    () => calculateCostPerLead(selectedPreviousPeriodAverageFact?.spend ?? null, selectedPreviousPeriodAverageFact?.leads ?? null),
    [selectedPreviousPeriodAverageFact?.leads, selectedPreviousPeriodAverageFact?.spend]
  )

  const selectedThreePeriodsAverageFactCostPerLead = useMemo(
    () => calculateCostPerLead(selectedThreePeriodsAverageFact?.spend ?? null, selectedThreePeriodsAverageFact?.leads ?? null),
    [selectedThreePeriodsAverageFact?.leads, selectedThreePeriodsAverageFact?.spend]
  )

  const summary = useMemo(() => {
    if (!selectedWeek) {
      return {
        plannedLeads: 0,
        plannedSpend: 0,
        targetologistsCount: 0
      }
    }

    return rowsForSelectedWeek.reduce((acc, row) => {
      if (row.status !== 'planned') return acc
      acc.plannedLeads += row.planLeads || 0
      acc.plannedSpend += row.planSpend || 0
      acc.targetologistsCount += 1
      return acc
    }, {
      plannedLeads: 0,
      plannedSpend: 0,
      targetologistsCount: 0
    })
  }, [rowsForSelectedWeek, selectedWeek])

  const selectedWeekIndex = selectedWeek
    ? weeks.findIndex((week) => week.week_number_in_period === selectedWeek.week_number_in_period)
    : -1

  const scenarioTargetWeeks = selectedWeekIndex >= 0
    ? weeks.slice(selectedWeekIndex)
    : []

  const updateWeekEntry = (
    targetKey: string,
    weekNumber: number,
    updater: (current: WeeklyPlanEntry, inherited: EffectiveWeeklyPlan) => WeeklyPlanEntry
  ) => {
    setDraft((current) => {
      const targetDraft = current[targetKey] || {}
      const currentEntry = targetDraft[weekNumber] || {}
      const inherited = getEffectiveWeeklyPlan(targetKey, weekNumber, current, weeks)
      const nextEntry = updater(currentEntry, inherited)
      return {
        ...current,
        [targetKey]: {
          ...targetDraft,
          [weekNumber]: nextEntry
        }
      }
    })
  }

  const handleStatusChange = (status: WeeklyPlanningStatus) => {
    if (!selectedTarget || !selectedWeek) return

    updateWeekEntry(selectedTarget.key, selectedWeek.week_number_in_period, (_current, inherited) => ({
      status,
      leads: status === 'planned' ? (inherited.leads ?? 0) : undefined,
      spend: status === 'planned' ? (inherited.spend ?? 0) : undefined
    }))
    if (activeTab !== 'all') {
      setActiveTab('all')
    }
    setSelectedScenarioMode(null)
  }

  const handlePlanValueChange = (field: 'leads' | 'spend', rawValue: string) => {
    if (!selectedTarget || !selectedWeek) return

    const parsed = parseNumericInput(rawValue)
    updateWeekEntry(selectedTarget.key, selectedWeek.week_number_in_period, (current, inherited) => ({
      status: current.status || inherited.status === 'excluded' ? 'planned' : 'planned',
      leads: field === 'leads' ? parsed : (current.leads !== undefined ? current.leads : inherited.leads),
      spend: field === 'spend' ? parsed : (current.spend !== undefined ? current.spend : inherited.spend)
    }))
    setSelectedScenarioMode(null)
  }

  const handleSave = async () => {
    if (!periods.currentPeriod || !Number.isFinite(parsedPartnerNumber)) return

    setSaving(true)
    try {
      const { data: userData } = await supabase.auth.getUser()
      const payload = flattenDraftToWeeklyPlanRows(
        draft,
        weeks,
        parsedPartnerNumber,
        userData.user?.id || null
      ).map(({ updated_at, partner_number, ...item }) => item)

      const { error: saveError } = await withAuthRecovery(() =>
        (supabase as any).rpc('save_click_weekly_plan', {
          p_partner_number: parsedPartnerNumber,
          p_period_id: periods.currentPeriod!.period_id,
          p_items: payload
        })
      )

      if (saveError) throw saveError
      setSavedDraft(draft)
    } catch (saveError) {
      console.error('❌ Не удалось сохранить маркетинговый план:', saveError)
      alert('Не удалось сохранить недельный план маркетинга')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setDraft(savedDraft)
    setSelectedScenarioMode(null)
  }

  const applyAverageFactScenario = () => {
    if (!selectedTarget || !selectedWeek) return

    const today = getTodayIso()
    const completedWeeks = weeks.filter((week) => week.week_end < today)
    if (completedWeeks.length === 0) {
      alert('В этом периоде пока нет завершенных недель с фактом')
      return
    }

    const completedFacts = completedWeeks
      .map((week) => selectedTargetFacts[week.week_number_in_period] || createEmptyFactSummary())
    const totalDays = completedWeeks.reduce((sum, week) => sum + week.week_len_days, 0)
    const totalLeads = completedFacts.reduce((sum, fact) => sum + fact.leads, 0)
    const totalSpend = completedFacts.reduce((sum, fact) => sum + fact.spend, 0)

    setDraft((current) => {
      const targetDraft = { ...(current[selectedTarget.key] || {}) }
      scenarioTargetWeeks.forEach((week) => {
        const scale = totalDays > 0 ? week.week_len_days / totalDays : 0
        targetDraft[week.week_number_in_period] = {
          status: 'planned',
          leads: Math.round((totalLeads * scale + Number.EPSILON) * 100) / 100,
          spend: Math.round((totalSpend * scale + Number.EPSILON) * 100) / 100
        }
      })

      return {
        ...current,
        [selectedTarget.key]: targetDraft
      }
    })
    setSelectedScenarioMode('average')
  }

  const applyRebalanceScenario = () => {
    if (!selectedTarget || !selectedWeek) return

    const currentWeekStates = weekStatesByTarget.get(selectedTarget.key) || {}
    const targetWeeks = scenarioTargetWeeks
    if (targetWeeks.length === 0) return

    const totalPlannedLeads = weeks.reduce((sum, week) => {
      const state = currentWeekStates[week.week_number_in_period]
      return state?.status === 'planned' ? sum + (state.leads || 0) : sum
    }, 0)
    const totalPlannedSpend = weeks.reduce((sum, week) => {
      const state = currentWeekStates[week.week_number_in_period]
      return state?.status === 'planned' ? sum + (state.spend || 0) : sum
    }, 0)

    const fixedWeeks = weeks.filter((week) => week.week_number_in_period < selectedWeek.week_number_in_period)
    const fixedLeads = fixedWeeks.reduce((sum, week) => {
      const state = currentWeekStates[week.week_number_in_period]
      if (week.week_end < getTodayIso()) {
        return sum + (selectedTargetFacts[week.week_number_in_period]?.leads || 0)
      }
      return state?.status === 'planned' ? sum + (state.leads || 0) : sum
    }, 0)
    const fixedSpend = fixedWeeks.reduce((sum, week) => {
      const state = currentWeekStates[week.week_number_in_period]
      if (week.week_end < getTodayIso()) {
        return sum + (selectedTargetFacts[week.week_number_in_period]?.spend || 0)
      }
      return state?.status === 'planned' ? sum + (state.spend || 0) : sum
    }, 0)

    const remainingLeads = Math.max(0, totalPlannedLeads - fixedLeads)
    const remainingSpend = Math.max(0, totalPlannedSpend - fixedSpend)
    const weights = targetWeeks.map((week) => week.week_len_days)
    const allocatedLeads = allocateProportionally(remainingLeads, weights)
    const allocatedSpend = allocateProportionally(remainingSpend, weights)

    setDraft((current) => {
      const targetDraft = { ...(current[selectedTarget.key] || {}) }
      targetWeeks.forEach((week, index) => {
        targetDraft[week.week_number_in_period] = {
          status: 'planned',
          leads: allocatedLeads[index] ?? 0,
          spend: allocatedSpend[index] ?? 0
        }
      })

      return {
        ...current,
        [selectedTarget.key]: targetDraft
      }
    })
    setSelectedScenarioMode('rebalance')
  }

  const restoreFromSavedBaseline = () => {
    if (!selectedTarget || !selectedWeek) return

    setDraft((current) => {
      const nextTargetDraft = { ...(current[selectedTarget.key] || {}) }
      const baselineTargetDraft = savedDraft[selectedTarget.key] || {}

      scenarioTargetWeeks.forEach((week) => {
        if (baselineTargetDraft[week.week_number_in_period]) {
          nextTargetDraft[week.week_number_in_period] = baselineTargetDraft[week.week_number_in_period]
        } else {
          delete nextTargetDraft[week.week_number_in_period]
        }
      })

      return {
        ...current,
        [selectedTarget.key]: nextTargetDraft
      }
    })
    setSelectedScenarioMode('keep')
  }

  if (periods.loading || loading || plansLoading) {
    return <LoadingScreen />
  }

  if (parsedStudioId && Number.isFinite(parsedStudioId)) {
    return <Navigate to={`/partners/${parsedPartnerNumber}/marketing/plan`} replace />
  }

  return (
    <div className="space-y-3">
      <Card className={`${SURFACE_CARD_CLASS} overflow-hidden p-0`}>
        <div className="space-y-2 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <Link
                to={`/partners/${parsedPartnerNumber}/marketing`}
                onClick={(event) => {
                  if (!confirmLeaveWithUnsavedChanges()) {
                    event.preventDefault()
                  }
                }}
                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Назад в обзор
              </Link>
              <div>
                <h1 className="text-lg font-semibold">План по таргетологам</h1>
                <p className="text-sm text-muted-foreground">
                  Выберите неделю, затем таргетолога и задайте для него статус, лиды и расход.
                </p>
              </div>
            </div>
          </div>

          <div className={`${SUBTLE_PANEL_CLASS} px-3 py-2 sm:px-4 sm:py-2.5`}>
            <PeriodSelector
              currentPeriod={periods.currentPeriod}
              onPrevPeriod={periods.goToPrevPeriod}
              onNextPeriod={periods.goToNextPeriod}
              canGoPrev={periods.canGoPrev}
              canGoNext={periods.canGoNext}
              isCurrentPeriod={periods.isCurrentPeriod}
              compact
            />
          </div>

          {weeks.length > 0 ? (
            <div className="overflow-x-auto">
              <div
                className="grid min-w-[760px] gap-2"
                style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}
              >
                {weeks.map((week) => {
                  const isSelected = week.week_number_in_period === selectedWeekNumber
                  const visualState = getWeekVisualState(week, todayIso)
                  return (
                    <button
                      key={week.week_number_in_period}
                      type="button"
                      onClick={() => setSelectedWeekNumber(week.week_number_in_period)}
                      className={`rounded-xl border px-3 py-2.5 text-left transition ${
                        isSelected
                          ? 'border-amber-300 bg-amber-50 text-amber-700'
                          : 'border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-0.5">
                            <div className="text-sm font-semibold leading-none">{formatWeekLabel(week)}</div>
                            <div className="text-xs opacity-80">{formatShortDate(week.week_start)} - {formatShortDate(week.week_end)}</div>
                          </div>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            visualState === 'past'
                              ? 'bg-slate-200 text-slate-700'
                              : visualState === 'current'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {visualState === 'past' ? 'Закрыта' : visualState === 'current' ? 'Текущая' : 'Будущая'}
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      {error ? (
        <Card className="border border-red-300">
          <p className="text-sm text-red-600">{error}</p>
        </Card>
      ) : null}

      <div className="grid gap-2 md:grid-cols-5">
        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-1 p-2.5">
            <div className="text-xs font-medium text-muted-foreground">Плановый расход</div>
            <div className="text-lg font-semibold">{formatMoneyValue(summary.plannedSpend, null)}</div>
          </div>
        </Card>
        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-1 p-2.5">
            <div className="text-xs font-medium text-muted-foreground">Плановые лиды</div>
            <div className="text-lg font-semibold">{formatNumber(summary.plannedLeads)}</div>
          </div>
        </Card>
        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-1 p-2.5">
            <div className="text-xs font-medium text-muted-foreground">Планируем</div>
            <div className="text-lg font-semibold">{formatNumber(tabCounts.planned)}</div>
          </div>
        </Card>
        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-1 p-2.5">
            <div className="text-xs font-medium text-muted-foreground">План не задан</div>
            <div className="text-lg font-semibold">{formatNumber(tabCounts.unset)}</div>
          </div>
        </Card>
        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-1 p-2.5">
            <div className="text-xs font-medium text-muted-foreground">Исключили</div>
            <div className="text-lg font-semibold">{formatNumber(tabCounts.excluded)}</div>
          </div>
        </Card>
      </div>

      <Card className={SURFACE_CARD_CLASS}>
        <div className="flex flex-wrap gap-2 p-2.5">
          {([
            ['all', 'Все'],
            ['planned', 'Планируем'],
            ['unset', 'План не задан'],
            ['excluded', 'Исключили']
          ] as Array<[PlannerTab, string]>).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setActiveTab(value)}
              className={`rounded-full px-4 py-2 text-sm font-medium ${
                activeTab === value
                  ? 'border border-primary bg-amber-50 text-amber-700'
                  : 'border border-border bg-card text-muted-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      <div className="grid gap-2.5 xl:grid-cols-[minmax(320px,380px)_minmax(0,1fr)]">
        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-2.5 p-3">
            <div>
              <h2 className="text-base font-semibold">
                {activeTab === 'all' ? 'Все таргетологи на неделю' : activeTab === 'planned' ? 'Планируем на неделю' : activeTab === 'excluded' ? 'Исключили на неделю' : 'План не задан на неделю'}
              </h2>
              <p className="text-sm text-muted-foreground">
                Слева список таргетологов по выбранной неделе. Выберите нужного, чтобы изменить его план.
              </p>
            </div>

            {visibleRows.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-muted px-4 py-6 text-sm text-muted-foreground">
                На выбранной неделе в этой категории пока никого нет.
              </div>
            ) : (
              <div className="space-y-2">
                {visibleRows.map((row) => {
                  const isSelected = row.key === selectedTargetKey
                  return (
                    <button
                      key={row.key}
                      type="button"
                      onClick={() => setSelectedTargetKey(row.key)}
                      className={`w-full rounded-xl border px-3 py-2.5 text-left transition ${
                        isSelected
                          ? 'border-primary bg-amber-50'
                          : 'border-border/60 bg-card hover:border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">{row.label}</div>
                          <div className="text-xs text-muted-foreground">{row.service}</div>
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${statusBadgeClass(row.status)}`}>
                          {statusLabel(row.status)}
                        </span>
                      </div>
                      <div className="mt-2 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
                        <div className="font-medium text-muted-foreground">План недели</div>
                        <div className="mt-1">Лиды: {row.planLeads === null ? '—' : formatNumber(row.planLeads)}</div>
                        <div>Расход: {row.planSpend === null ? '—' : formatMoneyValue(row.planSpend, row.currency)}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </Card>

        <Card className={SURFACE_CARD_CLASS}>
          <div className="space-y-2.5 p-3">
            {!selectedTarget || !selectedWeek || !selectedWeekPlan ? (
              <div className="rounded-xl border border-dashed border-border bg-muted px-4 py-6 text-sm text-muted-foreground">
                Выберите таргетолога слева, чтобы задать статус и недельный план.
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-border/60 bg-card p-3">
                  <div className="space-y-2.5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <h2 className="text-base font-semibold">{selectedTarget.label}</h2>
                        <p className="text-sm text-muted-foreground">{selectedTarget.service}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="text-right">
                          <div className="text-sm font-semibold">{formatWeekLabel(selectedWeek)}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatShortDate(selectedWeek.week_start)} - {formatShortDate(selectedWeek.week_end)}
                          </div>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusBadgeClass(selectedWeekPlan.status)}`}>
                          {statusLabel(selectedWeekPlan.status)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2.5">
                      <div className="flex flex-wrap gap-2">
                        {([
                          ['planned', 'Планируем'],
                          ['unset', 'План не задан'],
                          ['excluded', 'Исключили']
                        ] as Array<[WeeklyPlanningStatus, string]>).map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => handleStatusChange(value)}
                            disabled={selectedWeekIsReadOnly}
                            className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                              selectedWeekPlan.status === value
                                ? 'border-primary bg-amber-50 text-amber-700'
                                : 'border-border bg-card text-muted-foreground'
                            } disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {selectedWeekIsReadOnly ? (
                    <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      Неделя завершена. Только просмотр.
                    </div>
                  ) : null}

                  {selectedWeekPlan.status === 'planned' ? (
                    <div className="space-y-3">
                      <div className="grid gap-2 md:grid-cols-3">
                        <label className="space-y-1">
                          <span className="text-sm font-medium">Плановые лиды</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={selectedWeekPlan.leads === null ? '' : String(selectedWeekPlan.leads)}
                            onChange={(event) => handlePlanValueChange('leads', event.target.value)}
                            className={INPUT_CLASS}
                            placeholder="0"
                            disabled={selectedWeekIsReadOnly}
                          />
                          <PreviousPeriodMetricHint
                            planValue={selectedPreviousPeriodAveragePlan?.leads !== null && selectedPreviousPeriodAveragePlan?.leads !== undefined ? `${formatNumber(selectedPreviousPeriodAveragePlan.leads)} лида` : 'нет данных'}
                            factValue={selectedPreviousPeriodAverageFact ? `${formatNumber(selectedPreviousPeriodAverageFact.leads)} лида` : 'нет данных'}
                            fact3PeriodsValue={selectedThreePeriodsAverageFact ? `${formatNumber(selectedThreePeriodsAverageFact.leads)} лида` : 'нет данных'}
                          />
                        </label>
                        <label className="space-y-1">
                          <span className="text-sm font-medium">Плановый расход</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={selectedWeekPlan.spend === null ? '' : String(selectedWeekPlan.spend)}
                            onChange={(event) => handlePlanValueChange('spend', event.target.value)}
                            className={INPUT_CLASS}
                            placeholder="0"
                            disabled={selectedWeekIsReadOnly}
                          />
                          <PreviousPeriodMetricHint
                            planValue={selectedPreviousPeriodAveragePlan?.spend !== null && selectedPreviousPeriodAveragePlan?.spend !== undefined ? formatMoneyValue(selectedPreviousPeriodAveragePlan.spend, selectedTarget.currency) : 'нет данных'}
                            factValue={selectedPreviousPeriodAverageFact ? formatMoneyByCurrencies(selectedPreviousPeriodAverageFact.spendByCurrency) : 'нет данных'}
                            fact3PeriodsValue={selectedThreePeriodsAverageFact ? formatMoneyByCurrencies(selectedThreePeriodsAverageFact.spendByCurrency) : 'нет данных'}
                          />
                        </label>
                        <div className="space-y-1">
                          <span className="text-sm font-medium">Цена лида</span>
                          <div className={`${INPUT_CLASS} flex items-center bg-muted text-muted-foreground`}>
                            {selectedWeekPlanCostPerLead === null
                              ? '—'
                              : formatMoneyValue(selectedWeekPlanCostPerLead, selectedTarget.currency)}
                          </div>
                          <PreviousPeriodMetricHint
                            planValue={selectedPreviousPeriodAveragePlanCostPerLead !== null ? formatMoneyValue(selectedPreviousPeriodAveragePlanCostPerLead, selectedTarget.currency) : 'нет данных'}
                            factValue={selectedPreviousPeriodAverageFactCostPerLead !== null ? formatMoneyValue(selectedPreviousPeriodAverageFactCostPerLead, selectedTarget.currency) : 'нет данных'}
                            fact3PeriodsValue={selectedThreePeriodsAverageFactCostPerLead !== null ? formatMoneyValue(selectedThreePeriodsAverageFactCostPerLead, selectedTarget.currency) : 'нет данных'}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-border bg-card px-4 py-4 text-sm text-muted-foreground">
                      План на неделю не заполняется.
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-border/60 bg-card p-2.5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">Сценарии для выбранной и следующих недель</div>
                      <div className="text-xs text-muted-foreground">
                        Быстрые действия для выбранной недели и недель после нее.
                      </div>
                    </div>
                    {selectedScenarioMode ? (
                      <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                        Режим: {selectedScenarioMode === 'keep' ? 'Не менять' : selectedScenarioMode === 'rebalance' ? 'Выполнить план любой ценой' : 'Средний факт'}
                      </span>
                    ) : null}
                  </div>

                  <div className="grid gap-2 lg:grid-cols-3">
                    <button
                      type="button"
                      onClick={restoreFromSavedBaseline}
                      disabled={selectedWeekIsReadOnly}
                      className="rounded-lg border border-border bg-card px-2.5 py-2 text-left text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <div>Не менять</div>
                      <div className="mt-1 text-xs font-normal text-muted-foreground">Вернуть значения к последнему сохранению.</div>
                    </button>
                    <button
                      type="button"
                      onClick={applyRebalanceScenario}
                      disabled={selectedWeekIsReadOnly}
                      className="rounded-lg border border-border bg-card px-2.5 py-2 text-left text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <div>Выполнить план любой ценой</div>
                      <div className="mt-1 text-xs font-normal text-muted-foreground">Перераспределить остаток плана на выбранную и следующие недели.</div>
                    </button>
                    <button
                      type="button"
                      onClick={applyAverageFactScenario}
                      disabled={selectedWeekIsReadOnly}
                      className="rounded-lg border border-border bg-card px-2.5 py-2 text-left text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <div>Средний факт</div>
                      <div className="mt-1 text-xs font-normal text-muted-foreground">Подставить средний факт завершенных недель.</div>
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-border/60 bg-card p-2.5">
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">Весь период по таргетологу</div>
                      <div className="text-xs text-muted-foreground">
                        Нажмите на неделю, чтобы переключить экран на нее.
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleReset}
                        disabled={!hasUnsavedChanges || saving}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Сбросить изменения
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={!hasUnsavedChanges || saving}
                        /* Главное действие экрана — роль `primary`, как во
                           всём приложении. Янтарной ступенью лестницы было
                           выражено ровно это, но своим набором классов. */
                        className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {saving ? 'Сохранение...' : 'Сохранить план'}
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <div
                      className="grid min-w-[860px] gap-2"
                      style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}
                    >
                    {weeks.map((week) => {
                      const state = selectedTargetWeekStates[week.week_number_in_period] || { status: 'unset', leads: null, spend: null }
                      const fact = selectedTargetFacts[week.week_number_in_period] || createEmptyFactSummary()
                      const isCurrent = week.week_number_in_period === selectedWeekNumber
                      const visualState = getWeekVisualState(week, todayIso)

                      return (
                        <button
                          key={week.week_number_in_period}
                          type="button"
                          onClick={() => setSelectedWeekNumber(week.week_number_in_period)}
                          className={`min-h-[118px] rounded-xl border px-3 py-2.5 text-left transition ${
                            isCurrent
                              ? 'border-amber-300 bg-amber-50 '
                              : 'border-border/60 bg-muted hover:border-border hover:bg-card'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold">{formatWeekLabel(week)}</div>
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {formatShortDate(week.week_start)} - {formatShortDate(week.week_end)}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                visualState === 'past'
                                  ? 'bg-slate-200 text-slate-700'
                                  : visualState === 'current'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {visualState === 'past' ? 'Просмотр' : 'Редактирование'}
                              </span>
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${statusBadgeClass(state.status)}`}>
                                {statusLabel(state.status)}
                              </span>
                            </div>
                          </div>
                          <div className="mt-3 overflow-hidden rounded-lg border border-border/60 bg-card text-xs text-muted-foreground">
                            <div className="grid grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 bg-muted px-2.5 py-2 text-[11px] font-medium text-muted-foreground">
                              <div />
                              <div>План</div>
                              <div>Факт</div>
                            </div>
                            <div className="grid grid-cols-[72px_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-2 px-2.5 py-2.5">
                              <div className="font-medium text-muted-foreground">Лиды</div>
                              <div>
                                {state.status === 'planned'
                                  ? formatLeadLabel(state.leads)
                                  : '—'}
                              </div>
                              <div>{formatLeadLabel(fact.leads)}</div>

                              <div className="font-medium text-muted-foreground">Расход</div>
                              <div>
                                {state.status === 'planned'
                                  ? formatMoneyValue(state.spend || 0, selectedTarget.currency)
                                  : '—'}
                              </div>
                              <div>{formatMoneyByCurrencies(fact.spendByCurrency)}</div>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
